import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ROLE_LABEL } from "@/lib/status";
import NotificationBell from "@/components/app/NotificationBell";
import PushToggle from "@/components/app/PushToggle";
import ChangePasswordDialog from "@/components/app/ChangePasswordDialog";
import PixSettingsDialog from "@/components/app/PixSettingsDialog";
import { LogOut, KeyRound, QrCode } from "lucide-react";

export default function Shell({ title, subtitle, actions, children }) {
  const { user, logout } = useAuth();
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pixOpen, setPixOpen] = useState(false);
  const canPix = user?.role === "manager" || user?.role === "franchisee";

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <img src="/logo-clima-branca-icon.png" alt="Clima Branca"
              className="h-10 w-10 rounded-lg object-cover flex-shrink-0 ring-1 ring-slate-200" />
            <div className="min-w-0">
              <p className="font-bold text-[#0a2a6c] leading-tight truncate">Clima Branca</p>
              <p className="text-[11px] font-semibold tracking-wide text-[#1e7be0] leading-tight uppercase">Franquias</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            {["customer", "contract_customer", "technician", "franchisee"].includes(user?.role) && <PushToggle />}
            {canPix && (
              <Button variant="ghost" size="icon" onClick={() => setPixOpen(true)}
                data-testid="pix-settings-button" title="Meu Pix">
                <QrCode className="h-5 w-5 text-slate-600" />
              </Button>
            )}
            <div className="hidden sm:flex flex-col items-end">
              <span className="text-sm font-medium text-slate-700 truncate max-w-[160px]" data-testid="current-user-name">
                {user?.name}
              </span>
              <Badge variant="secondary" className="text-[10px] py-0">
                {ROLE_LABEL[user?.role] || user?.role}
              </Badge>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setPwdOpen(true)}
              data-testid="change-password-button"
              title="Trocar senha"
            >
              <KeyRound className="h-5 w-5 text-slate-600" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={logout}
              data-testid="logout-button"
              className="gap-1.5"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Sair</span>
            </Button>
          </div>
        </div>
      </header>

      <ChangePasswordDialog open={pwdOpen} onOpenChange={setPwdOpen} />
      {canPix && <PixSettingsDialog open={pixOpen} onOpenChange={setPixOpen} />}

      <main className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">{title}</h1>
            {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
          </div>
          {actions}
        </div>
        {children}
      </main>
    </div>
  );
}
